import {Component,OnInit,ViewChild} from '@angular/core';
import {WorkflowMotifsService} from "@services/admin/workflow/workflow-motifs.service";
import {Motif} from "@domain/workflow/motif";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {BehaviorSubject} from "rxjs";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {ActivatedRoute,Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {filter,finalize,first} from "rxjs/operators";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {NgForm} from "@angular/forms";
import {ToastrService} from "ngx-toastr";

/**
 * Composant du détail d'un motif
 *
 * @author Angeline Ha
 * @date 27/08/2024
 */
@Component({
  host: {'data-test-id': 'motif-detail'},
  templateUrl: './motif-detail.component.html'
})
export class MotifDetailComponent implements OnInit {
  /** Motif */
  motif: Motif;

  /** Formulaire */
  @ViewChild('form') form: NgForm;

  /** Indique si on est en création ou en modification */
  isCreation: boolean;

  /** Liste des différents onglets */
  listeTabItems: Array<PageHeaderItem> = [{
    code: Onglets.GENERALITES,
    libelle: this.translateService.instant('workflow.motif.detail.onglets.generalites')
  }];

  /** Onglet courant */
  selectedItem: PageHeaderItem = this.listeTabItems[0];

  /** Liste des actions possibles */
  listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

  /** Référence vers l'enum pour l'utiliser dans le template */
  Onglets = Onglets;

  /** Indique si on est en train d'enregistrer le motif */
  isSaving: boolean = false;

  /**
   * Constructeur
   *
   * @param router Router Angular
   * @param confirmService Service de confirmation
   * @param translateService Service des traductions
   * @param route Route active
   * @param toastrService Service des toasts
   * @param motifService Service des motifs
   */
  constructor(private router: Router,
              private confirmService: ConfirmService,
              private translateService: TranslateService,
              private route: ActivatedRoute,
              private toastrService: ToastrService,
              private motifService: WorkflowMotifsService
  ) {
  }

  /**
   * Initialisation
   */
  ngOnInit(): void {
    //Récupération de l'identifiant
    const idMotif = Number(this.route.snapshot.paramMap.get("idMotif"));

    //Mode création
    this.isCreation = idMotif === 0;

    //Initialisation du motif dont l'identifiant est récupéré dans la route
    this.initMotif(idMotif);

    //Définition de la liste des actions
    this.defineListeActions();
  }

  /**
   * Initialisation du motif
   *
   * @param idMotif Identifiant du motif à initialiser
   */
  private initMotif(idMotif: number) {
    //Si l'id est 0, c'est une création
    if (idMotif == 0) {
      //Création
      this.isCreation = true;

      //Nouveau motif
      this.motif = new Motif();
    } else {
      //Modification
      this.isCreation = false;

      //Si on a un id, on charge le motif
      this.motifService.getMotif(idMotif).pipe(first()).subscribe((result: Result) => {
        if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
          //Récupération du motif
          this.motif = result.data.motif;
        } else {
          //Toast d'erreur
          TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
        }
      });
    }
  }

  /**
   * Définition de la liste des actions
   */
  defineListeActions() {
    const liste: FloatingButtonAction[] = [{
      type: TypeAction.PRIMARY,
      icone: 'nio icon-sauvegarde',
      libelle: 'global.actions.enregistrer',
      doAction: () => this.saveMotif(),
      isDisabled: () => !this.form?.valid
    }];

    if (!this.isCreation) {
      //Ajout de l'action de suppression
      liste.push({
        type: TypeAction.SECONDARY,
        icone: 'nio icon-suppression',
        libelle: 'global.actions.supprimer',
        doAction: () => this.deleteMotif()
      });
    }

    //Définition de la liste des actions
    this.listeActions.next(liste);
  }

  /**
   * Changement d'onglet
   *
   * @param selectedItem Onglet sélectionné
   */
  onSelectedItemChange(selectedItem: PageHeaderItem) {
    //Mise à jour de l'onglet sélectionné
    this.selectedItem = selectedItem;
  }

  /**
   * Retour vers la liste des motifs
   */
  onGoBack() {
    // Retour à la liste des motifs
    this.router.navigate(['Admin/Workflow/Motifs/Motifs']);
  }

  /**
   * Enregistrement du motif
   */
  saveMotif() {
    //Enregistrement en cours
    this.isSaving = true;

    //Enregistrement du motif
    this.motifService.saveMotif(this.motif).pipe(finalize(() => this.isSaving = false)).subscribe((result: Result) => {
      if (result.codeErreur === TypeCodeErreur.NO_ERROR) {

        //Toast succès
        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

        if (this.isCreation) {
          //Récupération de l'identifiant du motif
          this.motif.idMotif = result.data.idMotif;

          //On n'est plus en mode création
          this.isCreation = false;

          //Ajout de l'action de suppression
          this.defineListeActions();

          //Navigation avec le bon id
          this.router.navigate(['../' + result.data.idMotif], {relativeTo: this.route});
        }
      } else {
        //Toast d'erreur
        TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
      }
    });
  }

  /**
   * Suppression du motif
   */
  deleteMotif() {
    //On demande une confirmation de suppression
    this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
        .pipe(filter(isConfirmed => isConfirmed))
        .subscribe(() => {
          //Suppression du motif
          this.motifService.deleteMotif(this.motif).pipe(first()).subscribe((result: Result)=> {
            if (result.codeErreur === TypeCodeErreur.NO_ERROR) {
              //Toast succès
              this.toastrService.success(this.translateService.instant('global.success.suppression'));

              //Retour à la liste
              this.onGoBack();
            } else {
              //Toast d'erreur
              TypeCodeErreur.showError(result.codeErreur,this.translateService,this.toastrService);
            }
          })
        });
  }
}

/**
 * Enum des différents onglets d'un motif
 */
export enum Onglets {
  GENERALITES = "Généralités"
}
